import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Input, Modal, Form, message, Button, Breadcrumb, Grid, Card, Row, Col,
  Statistic, Tag, Tooltip, Switch, Space
} from 'antd';
import { vehicleBrandsService } from '../../common/services';
import {
  EditOutlined, DeleteOutlined, RightOutlined, SearchOutlined,
  CarOutlined, CalendarOutlined, PlusOutlined, CheckOutlined,
  CloseOutlined, ArrowLeftOutlined, ImportOutlined
} from '@ant-design/icons';
import { Link, useParams, useNavigate } from 'react-router-dom';

const VehicleModels = () => {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const [brand, setBrand] = useState(null);
  const [models, setModels] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });

  // Modal d'ajout/édition
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);
  const [form] = Form.useForm();

  // Modal d'import
  const [isImportModalOpen, setImportModalOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importLoading, setImportLoading] = useState(false);

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const controlSize = isMobile ? 'small' : 'middle';

  const calculateStats = useCallback((data) => {
    const active = data.filter(m => m.isActive !== false).length;
    setStats({
      total: data.length,
      active,
      inactive: data.length - active
    });
  }, []);

  useEffect(() => {
    fetchModels();
  }, [brandId]);

  const fetchModels = async () => {
    setLoading(true);
    try {
      const response = await vehicleBrandsService.getModelsByBrand(brandId);
      if (response?.brand) {
        setBrand(response.brand);
        const modelsData = response.models || [];
        setModels(modelsData);
        calculateStats(modelsData);
      } else {
        message.error('Marque non trouvée');
        navigate('/dashboard/vehicle-brands');
      }
    } catch (err) {
      console.error('Erreur:', err);
      message.error('Erreur lors du chargement des modèles');
    } finally {
      setLoading(false);
    }
  };

  const filteredModels = models.filter((model) =>
    model.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setSelectedModel(null);
    form.resetFields();
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setSelectedModel(record);
    form.setFieldsValue({ name: record.name });
    setModalOpen(true);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Supprimer le modèle',
      content: `Voulez-vous vraiment supprimer le modèle "${record.name}" ?`,
      okText: 'Supprimer',
      okType: 'danger',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          const response = await vehicleBrandsService.deleteModel(record._id);
          if (response?.success) {
            message.success('Modèle supprimé');
            fetchModels();
          } else {
            message.error(response?.message || 'Erreur');
          }
        } catch (error) {
          message.error(error?.message || 'Erreur');
        }
      },
    });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (selectedModel) {
        const response = await vehicleBrandsService.updateModel(selectedModel._id, values);
        if (response?.success) {
          message.success('Modèle mis à jour');
          setModalOpen(false);
          fetchModels();
        } else {
          message.error(response?.message || 'Erreur');
        }
      } else {
        const response = await vehicleBrandsService.addModel({
          name: values.name,
          brandId: brandId
        });
        if (response?.success) {
          message.success('Modèle créé');
          setModalOpen(false);
          fetchModels();
        } else {
          message.error(response?.message || 'Erreur');
        }
      }
    } catch (error) {
      message.error(error?.message || 'Erreur');
    }
  };

  const handleToggle = async (record, checked) => {
    try {
      const response = await vehicleBrandsService.toggleModel(record._id, checked);
      if (response?.success) {
        message.success(checked ? 'Modèle activé' : 'Modèle désactivé');
        fetchModels();
      } else {
        message.error(response?.message || 'Erreur');
      }
    } catch (error) {
      message.error(error?.message || 'Erreur');
    }
  };

  const handleImport = async () => {
    if (!importText.trim()) {
      message.error('Veuillez entrer des modèles');
      return;
    }

    setImportLoading(true);
    try {
      const modelsList = importText.split('\n')
        .map(m => m.trim())
        .filter(m => m.length > 0);

      const response = await vehicleBrandsService.importModels(brandId, modelsList);
      if (response?.success) {
        message.success(response.message);
        setImportModalOpen(false);
        setImportText('');
        fetchModels();
      } else {
        message.error(response?.message || 'Erreur');
      }
    } catch (error) {
      message.error(error?.message || 'Erreur');
    } finally {
      setImportLoading(false);
    }
  };

  const columns = [
    {
      title: 'Modèle',
      key: 'name',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <CarOutlined style={{ fontSize: 20, color: '#1890ff' }} />
          <span style={{ fontWeight: 600, fontSize: 15 }}>{record.name}</span>
        </div>
      ),
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Date de création',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (date) => (
        <span style={{ color: '#595959' }}>
          <CalendarOutlined style={{ marginRight: 6, color: '#fa8c16' }} />
          {date}
        </span>
      )
    },
    {
      title: 'Statut',
      key: 'status',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Tooltip title={record.isActive !== false ? 'Actif' : 'Inactif'}>
          <Switch
            checked={record.isActive !== false}
            onChange={(checked) => handleToggle(record, checked)}
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
            size="small"
          />
        </Tooltip>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      align: 'center',
      render: (_, record) => (
        <Space>
          <Tooltip title="Modifier">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
              style={{ borderRadius: 6, color: '#faad14', borderColor: '#faad14' }}
            />
          </Tooltip>
          <Tooltip title="Supprimer">
            <Button
              danger
              type="text"
              icon={<DeleteOutlined />}
              size="small"
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="content-wrapper">
      <style>
        {`
          .stat-card-blue {
            background: linear-gradient(135deg, #1890ff 0%, #69c0ff 100%);
            border-radius: 12px;
            border: none;
          }
          .stat-card-blue .ant-statistic-title,
          .stat-card-blue .ant-statistic-content {
            color: white !important;
          }
        `}
      </style>

      <div className="container-xxl flex-grow-1" style={{ paddingTop: 8, paddingLeft: 24, paddingRight: 24 }}>
        <Breadcrumb
          separator={<RightOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />}
          style={{ marginBottom: 12 }}
        >
          <Breadcrumb.Item>
            <Link to="/dashboard" style={{ color: '#fa8c16' }}>Accueil</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/dashboard/vehicle-brands" style={{ color: '#fa8c16' }}>Marques</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <span style={{ color: '#595959' }}>Modèles de {brand?.name || '...'}</span>
          </Breadcrumb.Item>
        </Breadcrumb>

        {/* Header avec bouton retour */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/dashboard/vehicle-brands')}
          >
            Retour aux marques
          </Button>
          <Tag color="orange" style={{ fontSize: 16, padding: '4px 12px' }}>
            <CarOutlined style={{ marginRight: 8 }} />
            {brand?.name}
          </Tag>
        </div>

        {/* Statistiques */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={8}>
            <Card className="stat-card-blue" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><CarOutlined style={{ marginRight: 6 }} />Total</span>}
                value={stats.total}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={8}>
            <Card className="stat-card-blue" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><CheckOutlined style={{ marginRight: 6 }} />Actifs</span>}
                value={stats.active}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
          <Col xs={8}>
            <Card className="stat-card-blue" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
              <Statistic
                title={<span><CloseOutlined style={{ marginRight: 6 }} />Inactifs</span>}
                value={stats.inactive}
                valueStyle={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Carte principale */}
        <Card style={{ borderRadius: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
            <Col xs={24} sm={12}>
              <Input
                prefix={<SearchOutlined style={{ color: '#1890ff' }} />}
                placeholder="Rechercher un modèle..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                allowClear
                size={controlSize}
                style={{ borderRadius: 8 }}
              />
            </Col>
            <Col xs={24} sm={12} style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button
                icon={<ImportOutlined />}
                onClick={() => setImportModalOpen(true)}
                size={controlSize}
              >
                Import
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
                size={controlSize}
                style={{
                  background: 'linear-gradient(135deg, #1890ff 0%, #69c0ff 100%)',
                  border: 'none'
                }}
              >
                Ajouter un modèle
              </Button>
            </Col>
          </Row>

          <Table
            columns={columns}
            dataSource={filteredModels.map((model) => ({ ...model, key: model._id }))}
            pagination={{ pageSize: 15, showSizeChanger: true }}
            loading={isLoading}
            size={controlSize}
            scroll={{ x: 600 }}
          />
        </Card>
      </div>

      {/* Modal Ajout/Édition */}
      <Modal
        title={selectedModel ? 'Modifier le modèle' : 'Ajouter un modèle'}
        open={isModalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        okText={selectedModel ? 'Modifier' : 'Ajouter'}
        cancelText="Annuler"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Nom du modèle"
            name="name"
            rules={[{ required: true, message: 'Le nom est requis' }]}
          >
            <Input placeholder="Ex: Corolla, Swift, Civic..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Import */}
      <Modal
        title={`Importer des modèles pour ${brand?.name}`}
        open={isImportModalOpen}
        onCancel={() => setImportModalOpen(false)}
        onOk={handleImport}
        okText="Importer"
        cancelText="Annuler"
        confirmLoading={importLoading}
      >
        <p style={{ color: '#666', marginBottom: 16 }}>
          Entrez un modèle par ligne. Les modèles existants seront ignorés.
        </p>
        <Input.TextArea
          rows={10}
          placeholder="Corolla&#10;Yaris&#10;Camry&#10;..."
          value={importText}
          onChange={(e) => setImportText(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default VehicleModels;
